import { useRef, useCallback } from 'react';
import { compareXMLStates } from '@/utils/xmlDiff';

interface HistoryState {
  xmlString: string;
  timestamp: number;
  description?: string;
}

export function useUndoRedo(maxHistorySize: number = 50) {
  const historyRef = useRef<HistoryState[]>([]);
  const currentIndexRef = useRef(-1);

  const pushState = useCallback((xmlDoc: Document) => {
    const xmlString = new XMLSerializer().serializeToString(xmlDoc);

    // Remove any states after current index (if we're in the middle of history)
    if (currentIndexRef.current < historyRef.current.length - 1) {
      historyRef.current = historyRef.current.slice(0, currentIndexRef.current + 1);
    }

    const lastState = historyRef.current[historyRef.current.length - 1];
    if (lastState?.xmlString === xmlString) {
      currentIndexRef.current = historyRef.current.length - 1;
      return;
    }

    // Get the previous state from history for description calculation
    let previousDoc: Document | null = null;
    if (lastState) {
      const parser = new DOMParser();
      previousDoc = parser.parseFromString(lastState.xmlString, 'text/xml');
    }

    // Pre-calculate description for performance
    const description = compareXMLStates(previousDoc, xmlDoc);

    // Add new state
    historyRef.current.push({
      xmlString,
      timestamp: Date.now(),
      description
    });

    // Limit history size
    if (historyRef.current.length > maxHistorySize) {
      historyRef.current = historyRef.current.slice(-maxHistorySize);
    }

    currentIndexRef.current = historyRef.current.length - 1;
  }, [maxHistorySize]);

  const undo = useCallback((): Document | null => {
    if (currentIndexRef.current <= 0) {
      return null;
    }

    currentIndexRef.current--;
    const previousState = historyRef.current[currentIndexRef.current];
    
    // Parse and return the previous state
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(previousState.xmlString, 'text/xml');
    
    return newDoc;
  }, []);

  const redo = useCallback((): Document | null => {
    if (currentIndexRef.current >= historyRef.current.length - 1) {
      return null;
    }

    currentIndexRef.current++;
    const nextState = historyRef.current[currentIndexRef.current];
    
    // Parse and return the next state
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(nextState.xmlString, 'text/xml');
    
    return newDoc;
  }, []);

  const canUndo = useCallback(() => {
    return currentIndexRef.current > 0;
  }, []);

  const canRedo = useCallback(() => {
    return currentIndexRef.current < historyRef.current.length - 1;
  }, []);

  const getHistory = useCallback(() => {
    return historyRef.current.map((state, index) => ({
      ...state,
      index
    }));
  }, []);

  const jumpToState = useCallback((targetIndex: number): Document | null => {
    if (targetIndex < 0 || targetIndex >= historyRef.current.length) {
      return null;
    }

    currentIndexRef.current = targetIndex;
    const targetState = historyRef.current[targetIndex];
    
    // Parse and return the target state
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(targetState.xmlString, 'text/xml');
    
    return newDoc;
  }, []);

  const clearHistory = useCallback(() => {
    historyRef.current = [];
    currentIndexRef.current = -1;
  }, []);

  const getCurrentIndex = useCallback(() => {
    return currentIndexRef.current;
  }, []);

  return {
    pushState,
    undo,
    redo,
    canUndo,
    canRedo,
    getHistory,
    jumpToState,
    getCurrentIndex,
    clearHistory
  };
}
